import React, { useState, useEffect } from "react";
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Loader2,
  Save,
  ArrowLeft,
  Shield,
  Check,
  X
} from 'lucide-react';
import { useNavigate } from "react-router-dom";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

export default function AssociationPasswordUpdate() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => { setLoading(false); }, []);

  const handleChange = (field, value) => {
    if (field === 'currentPassword') setCurrentPassword(value);
    if (field === 'newPassword') setNewPassword(value);
    if (field === 'confirmPassword') setConfirmPassword(value);
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    setSubmitStatus(null);
  };

  const getPasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    const score = Object.values(checks).filter(Boolean).length;
    return { checks, score };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const validateForm = () => {
    const newErrors = {};
    if (!currentPassword.trim()) newErrors.currentPassword = 'Please enter your current password.';
    if (!newPassword.trim()) newErrors.newPassword = 'Please enter a new password.';
    else if (newPassword.length < 8) newErrors.newPassword = 'Password must be at least 8 characters long.';
    else if (passwordStrength.score < 3) newErrors.newPassword = 'Password is too weak. Please include uppercase, lowercase, numbers, and special characters.';
    else if (newPassword === currentPassword) newErrors.newPassword = 'New password must be different from current password.';
    if (!confirmPassword.trim()) newErrors.confirmPassword = 'Please confirm your new password.';
    else if (newPassword !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { setSubmitStatus('error'); return; }
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      const currentUser = JSON.parse(localStorage.getItem("currentUser"));
      const email = currentUser?.email;
      if (!email) { setSubmitStatus('error'); setErrors({ submit: 'No logged-in user found.' }); return; }

      const response = await fetch(`${API_BASE_URL}/update_admin_password.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setSubmitStatus('success');
        setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setErrors({});
        setTimeout(() => { navigate('/adminsettings'); }, 2000);
      } else {
        setSubmitStatus('error');
        setErrors({ submit: data.message || 'Failed to update password. Please try again.' });
      }
    } catch (err) {
      console.error("Error updating password:", err);
      setSubmitStatus('error');
      setErrors({ submit: 'Failed to update password. Please try again.' });
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen p-4 md:p-8">
        <div className="text-center py-16">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500 text-lg">Loading password settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/adminsettings')} className="p-2 hover:bg-white rounded-lg transition-colors">
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <Lock className="w-8 h-8 text-teal-600" />
            <div>
              <h1 className="text-4xl font-bold text-gray-800">Update Password</h1>
              <p className="text-gray-500">Change your account password</p>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6" />
              <div>
                <h2 className="text-2xl font-bold">Password Security Update</h2>
                <p className="text-teal-100 mt-1">Create a strong password to protect your account</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-green-800 font-semibold">Password Updated Successfully!</p>
                  <p className="text-green-700 text-sm">Redirecting you back to settings...</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && errors.submit && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-red-800 font-semibold">Update Failed</p>
                  <p className="text-red-700 text-sm">{errors.submit}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Current Password */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <Lock className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Current Password</h3>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1">
                      <Lock className="w-4 h-4" />
                      Current Password *
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => handleChange('currentPassword', e.target.value)}
                      className={`w-full border rounded-lg shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                        errors.currentPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="Enter your current password"
                    />
                    <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.currentPassword && <p className="text-red-600 text-sm mt-1">{errors.currentPassword}</p>}
                </div>
              </div>

              {/* New Password */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <Lock className="w-5 h-5 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-800">New Password</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center gap-1">
                        <Lock className="w-4 h-4" />
                        New Password *
                      </div>
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => handleChange('newPassword', e.target.value)}
                        className={`w-full border rounded-lg shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                          errors.newPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="Enter your new password"
                      />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.newPassword && <p className="text-red-600 text-sm mt-1">{errors.newPassword}</p>}
                  </div>

                  {newPassword && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">Password Strength</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className={`flex items-center gap-1 ${passwordStrength.checks.length ? 'text-green-600' : 'text-gray-400'}`}>
                          {passwordStrength.checks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          At least 8 characters
                        </div>
                        <div className={`flex items-center gap-1 ${passwordStrength.checks.uppercase ? 'text-green-600' : 'text-gray-400'}`}>
                          {passwordStrength.checks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          Uppercase letter
                        </div>
                        <div className={`flex items-center gap-1 ${passwordStrength.checks.lowercase ? 'text-green-600' : 'text-gray-400'}`}>
                          {passwordStrength.checks.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          Lowercase letter
                        </div>
                        <div className={`flex items-center gap-1 ${passwordStrength.checks.number ? 'text-green-600' : 'text-gray-400'}`}>
                          {passwordStrength.checks.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          Number
                        </div>
                        <div className={`flex items-center gap-1 ${passwordStrength.checks.special ? 'text-green-600' : 'text-gray-400'}`}>
                          {passwordStrength.checks.special ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          Special character
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Strength</span>
                          <span>{passwordStrength.score}/5</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              passwordStrength.score <= 2 ? 'bg-red-500' :
                              passwordStrength.score <= 3 ? 'bg-yellow-500' :
                              passwordStrength.score <= 4 ? 'bg-blue-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center gap-1">
                        <Lock className="w-4 h-4" />
                        Confirm New Password *
                      </div>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => handleChange('confirmPassword', e.target.value)}
                        className={`w-full border rounded-lg shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500 transition ${
                          errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="Confirm your new password"
                      />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                <button type="button" onClick={() => navigate('/adminsettings')} className="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors" disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-semibold py-2 px-6 rounded-lg hover:from-teal-700 hover:to-teal-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
                  {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" />Updating Password...</>) : (<><Save className="w-4 h-4" />Update Password</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
